import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  ApprovePromptInput,
  BulkDeletePromptInput,
  PromptListFilterInput,
  PromptRow,
  PromptUploadInput,
  RejectPromptInput,
  TranscribeRequest,
  TranscribeResponse,
  TtsConvertRequest,
  TtsConvertResponse,
  TtsFinalizeRequest,
  TtsHistoryRow,
} from './types';

export const voiceLibraryKeys = {
  all: ['voice-library'] as const,
  prompts: (uid: string | number | null) =>
    ['voice-library', 'prompts', uid] as const,
  pendingPrompts: (uid: string | number | null) =>
    ['voice-library', 'pending-prompts', uid] as const,
  ttsHistory: (uid: string | number | null) =>
    ['voice-library', 'tts-history', uid] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: voiceLibraryKeys.all });
}

/* ---------------- Listings ---------------- */

/**
 * Voice library listing — endpoint differs by role (mirrors legacy
 * `ListVoiceFiles.getPrompts`):
 *
 *   ROLE_SADMIN / ROLE_SUB_SADMIN → POST /api/obd/prompt/list/filter
 *     Returns prompts across every user with full metadata (uploadTime,
 *     approvalTime, delete_date, deletedBy). The `remarks` body field is a
 *     free-text remarks-substring filter (legacy passes "-1" for "all");
 *     category filtering is applied client-side in the pages.
 *
 *   ROLE_USER / ROLE_ADMIN (reseller) → GET /api/obd/prompts/list/{userId}
 *     Backend reads `user_details.type` and short-circuits: a reseller gets
 *     every prompt owned by their child users; a plain user gets their own
 *     prompts (see CampaignDaoImpl.promptList).
 */
export function usePromptList() {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isSadminLike =
    role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;
  return useQuery({
    queryKey: voiceLibraryKeys.prompts(userId),
    queryFn: () => {
      if (isSadminLike) {
        return api.post<PromptRow[]>('/api/obd/prompt/list/filter', {
          userId: Number(userId),
          remarks: '-1',
        } as PromptListFilterInput);
      }
      return api.get<PromptRow[]>(`/api/obd/prompts/list/${userId}?includePaused=true&includeDescendants=true`);
    },
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (d) => ensureArray<PromptRow>(d),
  });
}

/** POST /api/obd/pending/prompt/list/filter — admin queue.
 *
 *  Auto-refreshes every 10s so the SADMIN review queue stays current as
 *  users upload new prompts (mirrors legacy `ListVoiceFiles` setInterval).
 */
export function usePendingPromptList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: voiceLibraryKeys.pendingPrompts(userId),
    queryFn: () =>
      api.post<PromptRow[]>('/api/obd/pending/prompt/list/filter', {
        userId: Number(userId),
        remarks: '-1',
      } as PromptListFilterInput),
    enabled: Boolean(userId),
    staleTime: 10_000,
    refetchInterval: 10_000,
    select: (d) => ensureArray<PromptRow>(d),
  });
}

/** GET /api/obd/tts/list/{userId} — TTS synthesis history. */
export function useTtsHistory() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: voiceLibraryKeys.ttsHistory(userId),
    queryFn: () =>
      api.get<TtsHistoryRow[]>(`/api/obd/tts/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (d) => ensureArray<TtsHistoryRow>(d),
  });
}

/* ---------------- Upload ---------------- */

/** POST /api/obd/promptupload — multipart audio upload. */
export function useUploadPrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: PromptUploadInput) => {
      const fd = new FormData();
      fd.append('waveFile', payload.waveFile);
      fd.append('userId', String(payload.userId));
      fd.append('fileName', payload.fileName);
      fd.append('promptCategory', payload.promptCategory);
            fd.append('fileType', payload.fileType);
      if (payload.ttsFlowOnly) fd.append('ttsFlowOnly', '1');
      return apiClient
        .post<{ message?: string }>('/api/obd/promptupload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: invalidate,
  });
}

/* ---------------- TTS synthesize ---------------- */

/** POST /api/obd/convertion/texttospeech — preview audio. The server writes
 *  a pending TTS row (status=0) and returns the downloadUrl. The user must
 *  call useFinalizeTts to promote it into the library, or useDiscardTts to
 *  throw it away. */
export function useConvertTextToSpeech() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: TtsConvertRequest) =>
      api.post<TtsConvertResponse>(
        '/api/obd/convertion/texttospeech',
        payload,
      ),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/change/tts/status — finalize a previewed TTS. */
export function useFinalizeTts() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: TtsFinalizeRequest) =>
      api.post<{ message?: string }>(
        '/api/obd/change/tts/status',
        payload,
      ),
    onSuccess: invalidate,
  });
}

/**
 * DELETE /api/obd/tts/{ttsId}/{userId} — discard a pending TTS without saving.
 *
 * The backend requires BOTH path params (see CampaignController.java —
 * `/tts/{ttsId}/{userId}` is the only DELETE mapping; the userId-less variant
 * is GET only). Earlier obd3-gui omitted the userId segment, which 404'd.
 */
export function useDiscardTts() {
  const invalidate = useInvalidate();
  const userId = useAuthStore((s) => s.userId);
  return useMutation({
    mutationFn: (ttsId: number) =>
      api.delete<{ message?: string }>(`/api/obd/tts/${ttsId}/${userId}`),
    onSuccess: invalidate,
  });
}

/* ---------------- Approve / reject ---------------- */

/** POST /api/obd/prompt/approve */
export function useApprovePrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: ApprovePromptInput) =>
      api.post<{ message?: string }>('/api/obd/prompt/approve', payload),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/prompt/reject */
export function useRejectPrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: RejectPromptInput) =>
      api.post<{ message?: string }>('/api/obd/prompt/reject', payload),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/prompt/pause */
export function usePausePrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (promptId: number) =>
      api.post<{ message?: string }>('/api/obd/prompt/pause', { promptId }),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/prompt/resume */
export function useResumePrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (promptId: number) =>
      api.post<{ message?: string }>('/api/obd/prompt/resume', { promptId }),
    onSuccess: invalidate,
  });
}

/* ---------------- Delete ---------------- */

/** DELETE /api/obd/prompt/{promptId}/{userId} */
export function useDeletePrompt() {
  const invalidate = useInvalidate();
  const userId = useAuthStore((s) => s.userId);
  return useMutation({
    mutationFn: (promptId: number) =>
      api.delete<{ message?: string }>(
        `/api/obd/prompt/${promptId}/${userId}`,
      ),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/prompt/bulk/delete — body: `{userId, promptIds: "1,2,3"}`. */
export function useBulkDeletePrompts() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: BulkDeletePromptInput) =>
      api.post<{ message?: string }>(
        '/api/obd/prompt/bulk/delete',
        payload,
      ),
    onSuccess: invalidate,
  });
}

/* ---------------- Transcribe to Hindi ---------------- */

/** POST /api/obd/prompt/transcribe — body `{promptId, gender}`. The server
 *  looks up the prompt's audio, calls the voice-to-Hindi service and returns
 *  the detected language, transcript and Hindi translation. */
export function useTranscribeAudio() {
  return useMutation({
    mutationFn: (payload: TranscribeRequest) =>
      api.post<TranscribeResponse>('/api/obd/prompt/transcribe', payload),
  });
}

/* ============================================================
 *  Edit TTS template
 * ============================================================ */

export interface EditTtsRequest {
  id: number;
  language: string;
  gender: string;
  textScript: string;
  /** Prompt category id (server stores as the FK). */
  category: string;
  filename: string;
  /** Length of textScript — used by the server for credit accounting. */
  characterCount: number;
  userId: number | string;
}

/**
 * POST /api/obd/edit/tts — update an existing TTS clip in place. Same
 * fields the legacy Voice Library EditTTS form sent.
 */
export function useEditTts() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (req: EditTtsRequest) =>
      api.post<{ message?: string }>('/api/obd/edit/tts', req),
    onSuccess: invalidate,
  });
}

/* ============================================================
 *  Prompt category catalog
 * ============================================================ */

export interface PromptCategoryRow {
  /** Internal id used for filter dropdowns / api submission. */
  id: number | string;
  /** Display label. */
  name: string;
}

/**
 * GET /api/obd/prompt/cat — server-managed list of prompt categories
 * (e.g. "Greeting", "Promo", "Survey"). Used by the prompt upload form
 * and the prompt-list category filter.
 */
export function usePromptCategories() {
  return useQuery({
    queryKey: ['voice-library', 'prompt-categories'] as const,
    queryFn: () => api.get<{ promptCatId: number; promptCategory: string }[]>('/api/obd/prompt/cat'),
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      ensureArray<{ promptCatId: number; promptCategory: string }>(data).map((row): PromptCategoryRow => ({
        id: row.promptCatId,
        name: row.promptCategory,
      })),
  });
}
